import React from 'react';
import { useTranslate } from '../contexts/I18nContext';

// Mirror of Landing.jsx category style helper
import {
  Camera, Music, MapPin, Users, Palette, PartyPopper,
  Dumbbell, Baby, Lightbulb, GraduationCap, Briefcase, Heart,
} from 'lucide-react';

const ICONS = {
  music: Music,
  camera: Camera,
  pin: MapPin,
  users: Users,
  palette: Palette,
  party: PartyPopper,
  dumbbell: Dumbbell,
  baby: Baby,
  lightbulb: Lightbulb,
  cap: GraduationCap,
  briefcase: Briefcase,
  heart: Heart,
};

const PALETTE = [
  '#0EA5E9', '#F97316', '#A855F7', '#22D3EE',
  '#EF4444', '#22C55E', '#F59E0B', '#8B5CF6', '#10B981',
];

const iconFromName = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('music')) return Music;
  if (n.includes('experiencia') || n.includes('turismo')) return Camera;
  if (n.includes('comunidad') || n.includes('taller')) return Users;
  if (n.includes('arte') || n.includes('cultura')) return Palette;
  if (n.includes('fiesta') || n.includes('comida')) return PartyPopper;
  if (n.includes('deporte') || n.includes('bienestar')) return Dumbbell;
  if (n.includes('familia') || n.includes('niño') || n.includes('ninos')) return Baby;
  if (n.includes('emprend') || n.includes('innovaci')) return Lightbulb;
  if (n.includes('educa')) return GraduationCap;
  if (n.includes('negocio')) return Briefcase;
  return Heart;
};

/**
 * Horizontal scrollable category chips with colored icons.
 * Used on Landing and Explore. Click a chip to set/unset the filter.
 */
const CategoryRail = ({ categories = [], selectedCategoryId = '', onSelect, className = '' }) => {
  const { translateText } = useTranslate();
  const handleClick = (catId) => {
    if (selectedCategoryId === catId) {
      onSelect?.('');
    } else {
      onSelect?.(catId);
    }
  };

  return (
    <div className={`flex gap-1 sm:gap-3 overflow-x-auto sm:overflow-visible sm:justify-between pb-2 ${className}`} style={{ scrollbarWidth: 'none' }} data-testid="category-rail">
      {/* "All" pill */}
      <button
        type="button"
        onClick={() => onSelect?.('')}
        className="group flex flex-col items-center gap-1 sm:gap-2 flex-1 min-w-0 sm:flex-1"
        title="Todas"
        data-testid="category-rail-all"
      >
        <span
          className={`flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full text-white transition-all duration-300 group-hover:scale-110 ${
            selectedCategoryId === '' ? 'ring-2 ring-offset-2 ring-[#00CC66] ring-offset-transparent' : ''
          }`}
          style={{ backgroundColor: '#00CC66', boxShadow: '0 8px 20px -4px #00CC6666' }}
        >
          <Heart className="w-4 h-4 sm:w-6 sm:h-6 md:w-7 md:h-7" strokeWidth={2.2} />
        </span>
        <span className="hidden sm:block text-[10px] md:text-xs font-semibold text-center leading-tight max-w-[88px]">
          {translateText('Todas')}
        </span>
      </button>

      {categories.slice(0, 9).map((cat, idx) => {
        const Icon = iconFromName(cat.name);
        const color = cat.color || PALETTE[idx % PALETTE.length];
        const active = selectedCategoryId === cat.id;
        return (
          <button
            key={cat.id}
            type="button"
            onClick={() => handleClick(cat.id)}
            className="group flex flex-col items-center gap-1 sm:gap-2 flex-1 min-w-0 sm:flex-1"
            title={cat.name}
            data-testid={`category-rail-${cat.id}`}
          >
            <span
              className={`flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full text-white transition-all duration-300 group-hover:scale-110 ${
                active ? 'ring-2 ring-offset-2 ring-[#00CC66] ring-offset-transparent' : ''
              }`}
              style={{ backgroundColor: color, boxShadow: `0 8px 20px -4px ${color}66` }}
            >
              <Icon className="w-4 h-4 sm:w-6 sm:h-6 md:w-7 md:h-7" strokeWidth={2.2} />
            </span>
            <span className="hidden sm:block text-[10px] md:text-xs font-semibold text-center leading-tight max-w-[88px]">
              {translateText(cat.name)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default CategoryRail;
